import sortBy from 'lodash/sortBy';
import { type FC } from 'react';

import { QueryState } from '@/api/components/QueryState';
import { useFundListQuery } from '@/api/funds/fundsApi';
import { OrderType } from '@/api/orderList/models/Order';
import { useOrderListQuery } from '@/api/orderList/orderListApi';
import { isNotReady } from '@/api/utils/isNotReady';
import { Divider } from '@/components/Divider';
import { Column, Row } from '@/components/Grid';
import { WithGap } from '@/components/WithGap';
import { useTexts } from '@/hooks/useTexts';

import { Heading } from './components/Heading';
import { OrderLinesTable } from './components/OrderLinesTable';
import { PaymentInformation } from './components/PaymentInformation';
import { SignatoriesTable } from './components/SignatoriesTable';

export const OrderList: FC = () => {
  const texts = useTexts();

  const orderListQuery = useOrderListQuery();
  const fundListQuery = useFundListQuery();

  if (isNotReady([orderListQuery, fundListQuery])) {
    return <QueryState query={[orderListQuery, fundListQuery]} />;
  }

  const ordersSorted = sortBy(
    orderListQuery.data,
    (order) => order.createdDate,
  ).reverse();

  return (
    <WithGap gap="64">
      {ordersSorted.map((order, index) => (
        <div key={order.orderId}>
          <WithGap gap="24">
            {/* Only display the line between orders. */}
            {index > 0 && <Divider size="2" />}
            <Heading order={order} />

            <Row>
              <Column $span={6}>
                <SignatoriesTable order={order} />
              </Column>
              <Column $span={6}>
                {order.type === OrderType.Subscription && (
                  <PaymentInformation order={order} />
                )}
              </Column>
            </Row>

            {order.type === OrderType.Switch ? (
              <>
                <WithGap gap="48">
                  <WithGap gap="16">
                    <h4>{texts.pages.orderList.fundTable.title.switchFrom}</h4>
                    <OrderLinesTable
                      data-test="switch-from"
                      orderLines={order.orderLines.filter(
                        (orderPlacement) =>
                          orderPlacement.type === OrderType.Redemption,
                      )}
                    />
                  </WithGap>

                  <WithGap gap="16">
                    <h4>{texts.pages.orderList.fundTable.title.switchTo}</h4>
                    <OrderLinesTable
                      data-test="switch-to"
                      orderLines={order.orderLines.filter(
                        (orderPlacement) =>
                          orderPlacement.type === OrderType.Subscription,
                      )}
                    />
                  </WithGap>
                </WithGap>
              </>
            ) : (
              <OrderLinesTable
                data-test="order-lines-table"
                orderLines={order.orderLines}
              />
            )}
          </WithGap>
        </div>
      ))}
    </WithGap>
  );
};
